// Re-handoff helper — when SL cookies have expired but Supabase still
// has a valid session, re-issue them via the cookie-handoff endpoint.
// Pulled out of ServiceLayerAuthProvider so the auth-check function
// stays scannable.
//
// ADR-037 resilience: Cloud Run rolls + cookie expiries shouldn't
// lock users out as long as Supabase agrees they're still signed in.
// Returns true if the re-handoff succeeded (caller should re-fetch
// the session); false otherwise.

import { getSupabaseClient } from '@appic/service-layer'

export interface ReHandoffDeps {
  hadEmail: boolean
  /**
   * Ends THIS caller's wait only (Codex P2 #2213): when it aborts, this call resolves `false` at once
   * while the SHARED handoff keeps running for every coalesced waiter. Never forwarded to the handoff
   * fetch — one cancelled waiter must not turn the others' real outcome into `false`. A caller passing a
   * signal checks it after the await: `false` then means "you stopped waiting", not "the handoff failed".
   */
  signal?: AbortSignal
}

/**
 * Bound of the SHARED handoff, retries and backoff included. It cannot borrow a caller's timeout (it
 * outlives any single caller), so it carries its own: long enough to ride out the 502/503 backoff below
 * (1 + 2 + 4 s — the Cloud Run restart it exists for) plus the four round-trips, short enough that a
 * coalesced waiter is never held indefinitely. On expiry the handoff resolves `false` (the overlay path).
 */
export const REHANDOFF_TIMEOUT_MS = 20_000

function emit(name: string, detail: Record<string, unknown>): void {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }))
  } catch {
    // audit event - never block auth
  }
}

/**
 * Single shared in-flight re-handoff promise.
 *
 * BUG-FE-SILENT-SESSION-JWT-DEGRADATION-2026-05-31: the mount session check
 * runs from three triggers (initial mount, the 10-minute background interval,
 * and `visibilitychange`). When SL cookies expire, two of these can detect the
 * 401 at once and BOTH call `attemptReHandoff`. Each reads the canonical SL
 * Supabase session, and POSTs the SAME `refresh_token` to
 * `/v1/auth/cookie-handoff`; the SL then calls `supabase.auth` with a token the
 * sibling already consumed → "Invalid Refresh Token: Already Used" and a 401
 * loop. De-dupe so overlapping re-handoffs share ONE round-trip — a transient
 * cookie-expiry re-hands-off exactly once. The slot clears on settle so a later
 * genuine expiry can re-hand-off again.
 */
let inFlightReHandoff: Promise<boolean> | null = null

export async function attemptReHandoff(deps: ReHandoffDeps): Promise<boolean> {
  const { hadEmail, signal } = deps
  // An already-cancelled caller neither starts nor joins a handoff.
  if (signal?.aborted) return false
  // Coalesce concurrent re-handoffs (mount + interval + visibilitychange + concurrent 401s).
  if (!inFlightReHandoff) {
    inFlightReHandoff = doReHandoff(hadEmail).finally(() => {
      inFlightReHandoff = null
    })
  }
  return awaitUnlessAborted(inFlightReHandoff, signal)
}

/** The shared outcome, or `false` the moment THIS caller's signal aborts — the shared run is untouched. */
function awaitUnlessAborted(shared: Promise<boolean>, signal: AbortSignal | undefined): Promise<boolean> {
  if (!signal) return shared
  return new Promise<boolean>((resolve, reject) => {
    const onAbort = () => resolve(false)
    signal.addEventListener('abort', onAbort, { once: true })
    shared.then(
      (outcome) => { signal.removeEventListener('abort', onAbort); resolve(outcome) },
      (err: unknown) => { signal.removeEventListener('abort', onAbort); reject(err) },
    )
  })
}

/** A backoff sleep that ends early when the handoff's own deadline fires (the loop then sees it). */
function backoff(ms: number, deadline: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    const done = () => { clearTimeout(timer); deadline.removeEventListener('abort', done); resolve() }
    const timer = setTimeout(done, ms)
    deadline.addEventListener('abort', done, { once: true })
  })
}

/** The one error the handoff fails with when its own bound fires — whichever step it interrupts. */
function boundExceeded(): Error {
  return new Error(`re-handoff exceeded its ${REHANDOFF_TIMEOUT_MS}ms bound`)
}

/**
 * `p`, or `boundExceeded()` the moment the handoff's own deadline fires (Codex P2 #2213). A step that never
 * settles — getSession() on a stuck storage lock or a hung supabase-js refresh — must not hold the SHARED
 * handoff, and every coalesced 401 behind it, past the bound: the rejection reaches doReHandoff's catch.
 */
function withinDeadline<T>(p: Promise<T>, deadline: AbortSignal): Promise<T> {
  if (deadline.aborted) return Promise.reject(boundExceeded())
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(boundExceeded())
    deadline.addEventListener('abort', onAbort, { once: true })
    p.then(
      (value) => { deadline.removeEventListener('abort', onAbort); resolve(value) },
      (err: unknown) => { deadline.removeEventListener('abort', onAbort); reject(err) },
    )
  })
}

async function doReHandoff(hadEmail: boolean): Promise<boolean> {
  // The handoff's OWN deadline (REHANDOFF_TIMEOUT_MS) — never a caller's signal.
  const deadline = new AbortController()
  const deadlineTimer = setTimeout(() => deadline.abort(), REHANDOFF_TIMEOUT_MS)

  try {
    // Codex P1 (#2213): read the session from the CANONICAL SL Supabase client — the cached
    // singleton built with storageKey `sb-clanda-sl`, where the app's authenticated session is
    // persisted. A default `createClient(url, key)` reads supabase-js's default key
    // (`sb-<ref>-auth-token`), which the app never writes, so the 401 refresh-once found no session
    // and raised the overlay at once. Same env names + config shape as authSession's
    // getFreshAccessToken, so getSupabaseClient's (url, key) cache guard resolves the SAME instance.
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY
    if (!supabaseUrl || !supabaseKey) return false

    const apiBase = import.meta.env.VITE_SERVICE_LAYER_BASE_URL || '/service'
    const sb = getSupabaseClient({ serviceLayerBaseUrl: apiBase, supabaseUrl, supabaseAnonKey: supabaseKey })
    const { data: sbSession } = await withinDeadline(sb.auth.getSession(), deadline.signal)
    if (!sbSession?.session?.access_token || !sbSession?.session?.refresh_token) {
      return false
    }

    console.warn('[ServiceLayerAuth] SL cookies expired but Supabase session valid — attempting re-handoff')
    emit('auth:re-handoff-attempt', { hadEmail, timestamp: new Date().toISOString() })

    const handoffBody = JSON.stringify({
      access_token: sbSession.session.access_token,
      refresh_token: sbSession.session.refresh_token,
      provider: sbSession.session.user?.app_metadata?.provider || 'azure',
    })

    // Retry on 502/503 — Cloud Run restarts during deploy should not
    // lock users out.
    let handoffRes: Response | null = null
    for (let attempt = 0; attempt < 4; attempt++) {
      if (deadline.signal.aborted) throw boundExceeded()
      handoffRes = await fetch(`${apiBase}/v1/auth/cookie-handoff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: handoffBody,
        signal: deadline.signal,
      })
      if (handoffRes.ok || (handoffRes.status !== 502 && handoffRes.status !== 503)) break
      if (attempt < 3) {
        console.warn(`[ServiceLayerAuth] Re-handoff got ${handoffRes.status}, retrying (${attempt + 1}/4)...`)
        await backoff(1000 * Math.pow(2, attempt), deadline.signal)
      }
    }

    if (handoffRes?.ok) {
      console.warn('[ServiceLayerAuth] Re-handoff successful, retrying session check')
      emit('auth:re-handoff-success', { hadEmail, timestamp: new Date().toISOString() })
      return true
    }
    return false
  } catch (rehandoffErr) {
    const message = rehandoffErr instanceof Error ? rehandoffErr.message : String(rehandoffErr)
    console.warn('[ServiceLayerAuth] Re-handoff failed:', message)
    emit('auth:re-handoff-failed', { error: message, timestamp: new Date().toISOString() })
    return false
  } finally {
    clearTimeout(deadlineTimer)
  }
}
